import React from 'react';

const MistralAI = () => (
  <article className="news-article-body">
    <p className="news-lead">
      Founded in Paris in April 2023, Mistral AI has become Europe's most significant AI lab —
      valued at over <strong>$14 billion</strong> by 2025. Its defining characteristic: a commitment
      to open-weight releases that rivals Meta's Llama in openness while matching frontier
      performance in reasoning and code. From Mixtral's sparse MoE breakthrough to Mistral Small 4's
      unified multimodal architecture, Mistral is redefining what "lean and open" can achieve.
    </p>

    <h2>The Founding Story</h2>
    <p>
      Mistral was founded by three researchers who left their positions at Google DeepMind and Meta:
      <strong> Arthur Mensch</strong> (Google DeepMind), <strong>Guillaume Lample</strong>, and
      <strong>Timothée Lacroix</strong> (both from Meta's AI research division). Their thesis: that
      a small, highly focused team with deep model architecture expertise could produce world-class
      models without the bureaucracy — or cost — of hyperscale labs.
    </p>

    <h2>Key Model Milestones</h2>
    <div className="news-timeline">
      <div className="news-timeline-item">
        <span className="news-timeline-date">December 2023</span>
        <div>
          <strong>Mixtral 8x7B</strong> — Sparse mixture-of-experts model: 46.7B total parameters,
          only 12.9B active per token. Matched or surpassed GPT-3.5 on most benchmarks with 6×
          faster inference than Llama 2 70B. Apache 2.0 licensed. Became the open-source
          baseline for the industry.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">2024</span>
        <div>
          <strong>Mistral Small series</strong> (24B), <strong>Pixtral</strong> (multimodal vision),
          <strong>Ministral 3/8/14B</strong> (edge-optimised), and
          <strong>Codestral</strong> (code generation). Each targeted a specific deployment need —
          from on-device inference to code assistant workloads.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">2025</span>
        <div>
          <strong>Magistral</strong> (extended reasoning), <strong>Devstral</strong> (agentic coding),
          and <strong>Voxtral</strong> (voice with zero-shot cloning across 9 languages).
          Mistral also launched <strong>Le Chat</strong>, its consumer-facing AI assistant.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">March 16, 2026</span>
        <div>
          <strong>Mistral Small 4</strong> — The flagship unification model.
        </div>
      </div>
    </div>

    <h2>Mistral Small 4 — The Unified Architecture (March 2026)</h2>
    <p>
      Mistral Small 4 is the most ambitious single model Mistral has shipped: a
      <strong> 119B-parameter MoE model</strong> with only 6B parameters active per token,
      unifying instruction following, reasoning, multimodal understanding, and coding into
      a single deployable artifact.
    </p>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">Total Parameters</span>
        <span className="news-spec-value">119B (MoE, 128 experts)</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Active Parameters</span>
        <span className="news-spec-value">6B per token</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Context Window</span>
        <span className="news-spec-value">256K tokens</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Inputs</span>
        <span className="news-spec-value">Text + Native Images</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Reasoning Mode</span>
        <span className="news-spec-value">Configurable reasoning_effort</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Speed</span>
        <span className="news-spec-value">3× RPS vs predecessor; –40% latency</span>
      </div>
    </div>
    <p>
      The configurable <code>reasoning_effort</code> parameter is notable: developers can
      dial inference-time compute up or down per request — spending more compute on hard
      problems and less on simple ones — without switching between different model endpoints.
    </p>

    <h2>Mistral's Open-Source Philosophy</h2>
    <p>
      Most Mistral models ship under <strong>Apache 2.0</strong> — the most permissive
      open-source licence, allowing commercial use, fine-tuning, and redistribution without
      restrictions. This stance makes Mistral the preferred foundation for European enterprise
      AI deployments where data localisation and model customisation are regulatory requirements.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        Mistral demonstrates that open-source AI from a focused team can compete with — and
        often surpass — the massive labs' general-purpose models on efficiency metrics. For
        enterprise deployers, Mistral's Apache 2.0 models provide a legally clean, customisable
        foundation that avoids dependency on any single API provider.
      </p>
    </div>
  </article>
);

export default MistralAI;
